import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import nodemailer from "nodemailer";

import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { catalogError, isAppErrorLike, toClientError } from "@/lib/errors";
import { buildOtpMessage, generateOtp, hashOtp } from "@/lib/email/otp-email";

const emailSchema = z.string().email().max(255);

/** How long a freshly issued code stays valid. */
const CODE_TTL_MS = 30 * 60 * 1000;

/** Nodemailer transporter, built once per isolate rather than per request. */
let transporter;

function getTransporter() {
  const user = process.env.GMAIL_USER;
  const pass = process.env.GMAIL_APP_PASSWORD;

  if (!user || !pass) return null;

  if (!transporter) {
    transporter = nodemailer.createTransport({
      host: "smtp.gmail.com",
      port: 465,
      secure: true,
      auth: { user, pass },
      // Suppress nodemailer's bulk-tool header. `X-Mailer: Nodemailer` is a
      // common fingerprint for automated mailers, and Gmail signs the message
      // with its own DKIM anyway; saying we use a bulk tool adds nothing.
      xMailer: false,
      // Gmail is slow under load; drop the connection cleanly instead of
      // hanging a first-signup request for a minute.
      connectionTimeout: 10_000,
      greetingTimeout: 10_000,
      socketTimeout: 20_000,
    });
  }
  return transporter;
}

/**
 * Resolve the mail transporter, or fail with a code that names the real problem.
 *
 * Missing credentials are a deployment mistake rather than a user error, so
 * they get their own catalog entry instead of hiding behind a generic UNKNOWN.
 *
 * @returns {import("nodemailer").Transporter} Configured transporter.
 */
function requireTransporter() {
  const mailer = getTransporter();

  if (!mailer) {
    console.error(
      "[OTP] GMAIL_USER / GMAIL_APP_PASSWORD are not set; cannot send verification email",
    );
    throw catalogError("EMAIL_NOT_CONFIGURED", { area: "auth.otp.send" });
  }

  return mailer;
}

async function sendOtpEmail({ to, name, code }) {
  const mailer = requireTransporter();

  const message = buildOtpMessage({
    to,
    name,
    code,
    tagline: "Quizzes that turn classrooms into game shows.",
  });

  await mailer.sendMail(message);
}

/**
 * Send a verification code.
 *
 * Sign-up used to cap sends at one per minute and five per hour per address.
 * Both caps are gone: they locked out the very users they were meant to
 * protect - a person who mistyped their address, or whose mail never arrived,
 * could only wait while an abuser with the address was equally throttled.
 * Abuse is bounded elsewhere (Supabase's own limits on sign-up, and the fact
 * that a code is worthless without the mailbox it was sent to).
 *
 * The row is written with a single upsert rather than a separate counter
 * insert followed by a code update: the earlier split omitted `hash` and
 * `expires_at`, which are `NOT NULL`, so the first request for any address
 * failed with a not-null violation.
 */
export const sendOtp = createServerFn({ method: "POST" })
  .validator((d) => {
    const schema = z.object({
      email: emailSchema,
      name: z.string().trim().min(1).max(100),
    });
    return schema.parse(d);
  })
  .handler(async ({ data }) => {
    const email = data.email.trim().toLowerCase();
    const name = data.name.trim();

    const now = new Date();

    const code = generateOtp();
    const hash = hashOtp(code);
    const expiresAt = new Date(now.getTime() + CODE_TTL_MS).toISOString();

    const { error: upsertErr } = await supabaseAdmin.from("email_otps").upsert(
      {
        email,
        hash,
        expires_at: expiresAt,
        attempts: 0,
        last_sent_at: now.toISOString(),
      },
      { onConflict: "email" },
    );

    if (upsertErr) {
      throw await toClientError(upsertErr, { area: "auth.otp.send" });
    }

    // The code is stored before the mail attempt so a failed send leaves the
    // row in a consistent state; a retry simply overwrites it with a new one.
    try {
      await sendOtpEmail({ to: email, name, code });
    } catch (e) {
      // `requireTransporter` already threw a catalog error naming the missing
      // configuration; anything else is an SMTP-level failure, which is a
      // genuine fault worth reporting.
      if (isAppErrorLike(e)) throw e;

      console.error("[OTP] Failed to send verification email:", e);
      throw catalogError("EMAIL_NOT_CONFIGURED", {
        area: "auth.otp.send",
        message: "We couldn't send the verification email. Please try again.",
      });
    }

    return { success: true };
  });
